import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/authStore';

// API base URL - uses /api prefix which nginx proxies to the backend
export const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Standard API envelope returned by every backend endpoint.
 * The response interceptor below unwraps this automatically so service
 * functions receive `T` directly in `response.data`.
 */
export interface ApiResponse<T> {
  success: boolean;
  data: T;
}

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const tokens = useAuthStore.getState().tokens;
  if (tokens?.accessToken) {
    config.headers.Authorization = `Bearer ${tokens.accessToken}`;
  }
  return config;
});

// ---------------------------------------------------------------------------
// Shared in-flight refresh
//
// When several requests fire in parallel with an expired access token, each
// one's response handler will hit the 401 branch below. Without coordination
// they would each POST /auth/refresh with the SAME refresh token, but only the
// first call wins — the server deletes the row on rotation, so the rest
// receive 401 and fall into the logout branch. The user is signed out
// mid-session even though they have a valid refresh token.
//
// The fix is a module-level promise. The first 401 starts the refresh; every
// subsequent 401 awaits the same promise instead of issuing another rotate.
// All requests then retry with the same fresh access token.
// ---------------------------------------------------------------------------

interface RefreshedTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

let pendingRefresh: Promise<RefreshedTokens | null> | null = null;

async function refreshTokens(refreshToken: string): Promise<RefreshedTokens | null> {
  try {
    const response = await axios.post<{ success: boolean; data: RefreshedTokens }>(
      '/api/auth/refresh',
      { refreshToken },
    );
    const newTokens = response.data.data;
    useAuthStore.setState({ tokens: newTokens });
    return newTokens;
  } catch {
    return null;
  }
}

// Response interceptor: unwrap { success, data } envelope then handle token refresh
api.interceptors.response.use(
  (response) => {
    // Unwrap the standard { success: boolean; data: T } envelope.
    // Only unwrap when the envelope shape is present — non-envelope endpoints
    // (e.g. VAPID key, unread count) pass through unchanged.
    if (
      response.data !== null &&
      typeof response.data === 'object' &&
      'success' in response.data &&
      'data' in response.data
    ) {
      return { ...response, data: response.data.data };
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      const tokens = useAuthStore.getState().tokens;
      if (!tokens?.refreshToken) {
        return Promise.reject(error);
      }

      // Share the refresh across concurrent 401s. The first caller starts the
      // request; subsequent callers await the same promise. Cleared in
      // `finally` so the next refresh cycle (8h later) can fire fresh.
      if (!pendingRefresh) {
        pendingRefresh = refreshTokens(tokens.refreshToken).finally(() => {
          pendingRefresh = null;
        });
      }

      const newTokens = await pendingRefresh;
      if (!newTokens) {
        useAuthStore.getState().logout();
        return Promise.reject(error);
      }

      originalRequest.headers = originalRequest.headers ?? {};
      originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
      return api(originalRequest);
    }

    // Preserve the original error with response data for specific error handling
    return Promise.reject(error);
  },
);

export default api;
